import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Stack,
  LinearProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layout/MainLayout";
import { feesApi } from "../api/feesApi";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import TodayIcon from "@mui/icons-material/Today";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BarChartIcon from "@mui/icons-material/BarChart";

type TrendPoint = {
  date: string;
  label: string;
  collection: number;
  refund: number;
  net: number;
};

const quickActions = [
  { label: "Fee Deposit", path: "/fees/deposit", icon: <AccountBalanceWalletIcon fontSize="small" /> },
  { label: "View Receipts", path: "/fees/view-fee-receipts", icon: <ReceiptLongIcon fontSize="small" /> },
  { label: "Daybook", path: "/fees/daybook-receipt", icon: <TodayIcon fontSize="small" /> },
  { label: "Discount", path: "/fees/fee-discount", icon: <LocalOfferIcon fontSize="small" /> },
  { label: "Refund", path: "/fees/fee-refund", icon: <ReplayIcon fontSize="small" /> },
  { label: "No Dues", path: "/fees/no-dues-receipt", icon: <VerifiedUserIcon fontSize="small" /> },
  { label: "Ledger Reports", path: "/fees/fees-ledger-reports", icon: <BarChartIcon fontSize="small" /> },
];

const toIsoDate = (value: Date) => value.toISOString().slice(0, 10);
const money = (value: number) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value || 0);
const pct = (value: number) => `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;

const Sparkline: React.FC<{ values: number[]; color: string }> = ({ values, color }) => {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const den = Math.max(values.length - 1, 1);
  const points = values
    .map((value, idx) => {
      const x = (idx / den) * 100;
      const y = 42 - (value / max) * 34;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg viewBox="0 0 100 44" width="100%" height="44" role="img" aria-label="trend chart">
      <polyline fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" points={points} />
    </svg>
  );
};

const FeesModuleHome: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [trend, setTrend] = React.useState<TrendPoint[]>([]);
  const [modeWise, setModeWise] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const today = new Date();
        const dates = Array.from({ length: 7 }).map((_, idx) => {
          const d = new Date(today);
          d.setDate(today.getDate() - (6 - idx));
          return toIsoDate(d);
        });

        const rows = await Promise.all(dates.map((date) => feesApi.daybook({ date }).catch(() => null)));
        const points = rows.map((row, idx) => ({
          date: dates[idx],
          label: new Date(dates[idx]).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
          collection: Number(row?.collection || 0),
          refund: Number(row?.refund || 0),
          net: Number(row?.net || 0),
        }));

        setTrend(points);
        setModeWise(rows[rows.length - 1]?.modeWise || {});
      } catch {
        setError("Unable to load fee analytics right now.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const today = trend[trend.length - 1] || { collection: 0, refund: 0, net: 0 };
  const first = trend[0] || { collection: 0, net: 0 };
  const prev = trend[trend.length - 2] || first;
  const collectionTotal = trend.reduce((sum, point) => sum + point.collection, 0);
  const refundTotal = trend.reduce((sum, point) => sum + point.refund, 0);
  const avgPerDay = trend.length ? collectionTotal / trend.length : 0;
  const growth = first.collection ? ((today.collection - first.collection) / first.collection) * 100 : 0;
  const netMomentum = prev.net ? ((today.net - prev.net) / Math.max(prev.net, 1)) * 100 : 0;
  const refundRate = collectionTotal ? (refundTotal / collectionTotal) * 100 : 0;
  const maxCollection = Math.max(...trend.map((point) => point.collection), 1);
  const modeRows = Object.entries(modeWise).sort((a, b) => b[1] - a[1]);

  return (
    <MainLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 45%, #0ea5e9 100%)",
            color: "#fff",
            overflow: "hidden",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                Fees Intelligence Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                SaaS-style finance analytics for collections, refunds, and payment behavior.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip icon={<TrendingUpIcon />} label={`7d Growth ${pct(growth)}`} sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }} />
              <Button variant="contained" color="secondary" onClick={() => navigate("/fees/daybook-receipt")}>
                Open Daybook
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={{ p: 2.2, borderRadius: 3, height: "100%" }}>
              <Typography variant="caption" color="text.secondary">Today Collection</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>Rs. {money(today.collection)}</Typography>
              <Typography variant="body2" sx={{ color: "success.main", mt: 0.5 }}>{pct(netMomentum)} vs previous day</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={{ p: 2.2, borderRadius: 3, height: "100%" }}>
              <Typography variant="caption" color="text.secondary">7-Day Collection</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>Rs. {money(collectionTotal)}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>Avg/day Rs. {money(avgPerDay)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={{ p: 2.2, borderRadius: 3, height: "100%" }}>
              <Typography variant="caption" color="text.secondary">7-Day Refund</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>Rs. {money(refundTotal)}</Typography>
              <Typography variant="body2" sx={{ color: "warning.main", mt: 0.5 }}>Refund rate {refundRate.toFixed(2)}%</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper sx={{ p: 2.2, borderRadius: 3, height: "100%" }}>
              <Typography variant="caption" color="text.secondary">Today Net</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>Rs. {money(today.net)}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>Net after refunds</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Collection Trend (Last 7 Days)</Typography>
                <Chip size="small" label={loading ? "Syncing..." : "Live from Daybook"} color={loading ? "warning" : "success"} />
              </Stack>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.2, minHeight: 210, mt: 1 }}>
                {trend.map((point) => (
                  <Box key={point.date} sx={{ flex: 1, textAlign: "center" }}>
                    <Box
                      sx={{
                        borderRadius: 1.5,
                        background: "linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)",
                        height: `${Math.max(10, (point.collection / maxCollection) * 170)}px`,
                        transition: "all 220ms ease",
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 0.6, display: "block" }}>{point.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{money(point.collection)}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Net Momentum</Typography>
              <Sparkline values={trend.map((point) => point.net)} color="#10b981" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.3 }}>
                Trend compares daily net collection after refund adjustment.
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2.2, mb: 1 }}>Payment Mode Split (Today)</Typography>
              <Stack spacing={1.2}>
                {modeRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No mode-wise data for today.</Typography>
                ) : (
                  modeRows.map(([mode, value]) => {
                    const progress = today.collection ? (value / today.collection) * 100 : 0;
                    return (
                      <Box key={mode}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{mode}</Typography>
                          <Typography variant="caption" color="text.secondary">Rs. {money(value)}</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={Math.min(progress, 100)} sx={{ height: 8, borderRadius: 999 }} />
                      </Box>
                    );
                  })
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.4 }}>Quick Actions</Typography>
              <Grid container spacing={1.2}>
                {quickActions.map((action) => (
                  <Grid key={action.path} item xs={12} sm={6} md={4} lg={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={action.icon}
                      onClick={() => navigate(action.path)}
                      sx={{
                        justifyContent: "flex-start",
                        py: 1.2,
                        px: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      {action.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default FeesModuleHome;
